<template>
  <div class="selected-items" :class="{ 'mt-4': readonly }">
    <custom-callout
      v-for="(item, idx) in items"
      :key="`item-${idx}`"
      class="mb-3"
      controlled
      :opened="item.isOpen"
      @click:open="openCloseHandler(idx, item)"
    >
      <div class="row py-2">
        <div
          class="col-12 col-md-2 align-self-stretch d-inline-flex flex-column align-items-center align-items-md-start justify-content-center"
          :class="{
            'border-right pr-4': readonly,
          }"
        >
          <span class="bold">
            {{ item.name }}
          </span>
          <template v-if="readonly">
            {{ item.itemNumber }}
            <span v-if="item.requisitionStatus" class="color-gray-6">
              <em class="fas fa-circle color-yellow mr-2" />
              {{ item.requisitionStatus }}
            </span>
          </template>
        </div>
        <div class="col-12 col-md pl-md-4 mt-3 mt-md-0">
          <validation-observer ref="articles">
            <div
              class="row mx-0"
              :class="
                readonly ? 'justify-content-between' : 'justify-content-around'
              "
            >
              <div v-if="!readonly" class="col-12 col-md-auto mb-2 mb-md-0">
                <div class="data-label">N&uacute;mero de Item:</div>
                <span class="data">{{ item.itemNumber }}</span>
              </div>
              <div class="col-12 col-md-auto mb-2 mb-md-0">
                <div class="data-label">Cantidad de Art&iacute;culos:</div>
                <span class="data">{{ item.quantity }}</span>
              </div>
              <div class="col-12 col-md-auto mb-2 mb-md-0">
                <div class="data-label">Precio por Art&iacute;culo:</div>
                <span class="data">{{ item.price | price }}</span>
              </div>
              <div class="col-12 col-md-auto mb-2 mb-md-0">
                <div class="data-label">Monto Total:</div>
                <span class="data">
                  {{ item.amount | price }}
                </span>
              </div>
              <div v-if="!readonly" class="col-12 col-md-auto mb-2 mb-md-0">
                <action-button
                  i="fas fa-times-circle fa-2x color-primary-17"
                  @click="$emit('on-remove-parent', idx)"
                />
              </div>
              <template v-if="readonly">
                <div class="col-12 col-md-auto mb-2 mb-md-0">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-drop-down
                      v-model="item.measure"
                      label="Unidad de Medida:"
                      :options="options.measureUnits"
                      fieldkey="id"
                      fieldname="name"
                      placeholder="Seleccione"
                      :clearable="false"
                      :disabled="isSubmitted"
                      :error="errors[0]"
                      custom-error-msg="La unidad de medida es requerida."
                    />
                  </validation-provider>
                </div>
                <div class="col-12 col-md-4 mb-2 mb-md-0">
                  <validation-provider v-slot="{ errors }" rules="required">
                    <custom-drop-down
                      v-model="item.purchaseOrder"
                      label="Cifra de Cuenta:"
                      :options="options.purchaseOrders"
                      fieldkey="id"
                      fieldname="name"
                      placeholder="Seleccione"
                      :clearable="false"
                      :disabled="isSubmitted"
                      :error="errors[0]"
                      custom-error-msg="La cifra de cuenta es requerida."
                    />
                  </validation-provider>
                </div>
              </template>
            </div>
          </validation-observer>
        </div>
      </div>
      <template #edition>
        <div class="secondary-13 py-1 rounded mt-1">
          <ResultRow
            v-for="(requests, index) in computedArticles(item)"
            :key="`request-$${index}`"
            :value="requests"
            readonly
            @on-remove="$emit('on-remove-child', idx, index)"
          />
          <pagination
            v-if="readonly && pagination.totalItemCount > pagination.pageSize"
            :data="pagination"
            show-text
            @pagination-go-page="onPageChange($event, item.id)"
          />
        </div>
      </template>
    </custom-callout>
  </div>
</template>

<script>
import ActionButton from 'ActionButton';
import CustomCallout from 'custom-callout';
import Pagination from 'pagination';
import CustomDropDown from 'custom-drop-down';
import ResultRow from './ResultRow';
import { price } from '@/utils/filters';
import measureUnits from '@/utils/constants/pei/materials-equipment-requisition/measureUnits.js';
import { getRequestedDetailArticles } from '@/modules/materials-equipment/api.js';
import { getPurchaseOrders } from '@/modules/common/code.api';

export default {
  name: 'SelectedItems',
  components: {
    CustomCallout,
    CustomDropDown,
    ResultRow,
    Pagination,
    ActionButton,
  },
  filters: { price },
  model: { prop: 'items' },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    readonly: {
      type: Boolean,
      default: () => false,
    },
    isSubmitted: {
      type: Boolean,
      default: () => false,
    },
    requisitionId: {
      type: Number,
      default: () => null,
    },
  },
  data: () => ({
    pagination: {
      currentPage: 1,
      totalItemCount: 0,
      pageCount: 1,
      count: 0,
      pageSize: 5,
    },
    options: {
      measureUnits,
      purchaseOrders: [],
    },
    articleRequests: [],
  }),
  computed: {
    computedArticles() {
      return ({ articles }) =>
        articles?.length && !this.readonly ? articles : this.articleRequests;
    },
  },
  async mounted() {
    if (this.readonly) await this.getPurchaseOrders();
  },
  methods: {
    async getPurchaseOrders() {
      const { data } = await getPurchaseOrders();
      this.options.purchaseOrders = [...data];
    },
    async getDetailArticles(articleId) {
      const params = {
        requisitionId: this.requisitionId,
        articleId: articleId,
        pageIndex: this.pagination.currentPage,
        pageSize: this.pagination.pageSize,
      };
      const { data: response } = await getRequestedDetailArticles(params);
      this.articleRequests = response.data;
      this.pagination = {
        ...this.pagination,
        totalItemCount: response.totalItemCount,
        count: response.count,
        pageCount: response.pageCount,
        currentPage: response.currentPage,
      };
    },
    async onPageChange(page, articleId) {
      this.pagination.currentPage = page;
      await this.getDetailArticles(articleId);
    },
    async openCloseHandler(index, { isOpen, id }) {
      const items = this.items.map((item, i) => ({
        ...item,
        isOpen: index === i ? !item.isOpen : false,
      }));
      this.articleRequests = [];
      if (!isOpen && this.readonly) await this.getDetailArticles(id);
      this.$emit('input', items);
    },
  },
};
</script>
